import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../components/ui/table';
import { FileText, Edit, Trash2, Upload, Wrench } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { MaterialData } from '../../../../types/materials';
import { WireFilters } from './Filters/WireFilters';
import { useWireFilters } from './Filters/hooks/useWireFilters';
import { Button } from '../../../../../../components/ui/button';
import { DeleteConfirmationDialog, DeleteItem } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { Pagination } from '../../../../../../components/ui/Pagination';
import { SortableTableHead } from '../../../../../../components/ui/SortableTableHead';
import { useTableSort } from '../../../../../../hooks/useTableSort';
import { deleteMaterials, DeleteMaterialDocument } from '../../../../../../redux/Project/materialSaga';
import { UpdateMaterialDialog } from '../UpdateMaterialDialog';
import { DocumentDownloadModal } from '../../specifications/DocumentDownloadModal';
import { MaterialDocumentUploadModal } from '../MaterialDocumentUploadModal';
import { DocumentTag } from '../../../../../../features/project/types';
import { formatDate } from '../../../../../../utils/unitUtils';

interface WireTabProps {
  materials: MaterialData[];
  selectedPipeSize: number;
  currentPage: number;
  materialsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  refreshMaterials: () => Promise<void>;
  onRowDelete?: (materialId: string | number, category: string) => Promise<void>;
  deletingRows?: Set<string>;
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  pipeSizesData: any;
  canEdit?: boolean;
  onOpenViewer?: (fileOrFiles: any | any[]) => void;
}

export const WireTab: React.FC<WireTabProps> = React.memo(({
  materials,
  selectedPipeSize,
  currentPage,
  materialsPerPage,
  onPageChange,
  onItemsPerPageChange,
  refreshMaterials,
  onRowDelete,
  deletingRows,
  documentTags,
  setDocumentTags,
  pipeSizesData,
  canEdit = true,
  onOpenViewer
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState<MaterialData | null>(null);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialData | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  // Use the wire filters hook
  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueLotNumbers,
    uniqueSerialNumbers,
    uniqueManufacturers,
    uniqueBatchNumbers,
    filteredMaterials,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = useWireFilters({ materials });

  // Filter materials to only show Wire category
  const wireMaterials = filteredMaterials.filter((material: MaterialData) => material.materialCategory === 'Wire Lot');

  // Add sorting functionality
  const { sortedData: sortedWireMaterials, handleSort, getSortDirection } = useTableSort(wireMaterials);


  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);

      if (onRowDelete) {
        await onRowDelete(itemToDelete?.materialId, itemToDelete?.materialCategory);
        setIsDeleteModalVisible(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      } else {
        const res = await deleteMaterials([itemToDelete?.materialId], itemToDelete?.materialCategory);
        if (res) {
          setIsDeleteModalVisible(false);
          setItemToDelete(null);
          setDeleteLoading(false);
          refreshMaterials();
        }
      }
    } catch (error) {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
      return null;
    }
  }

  const handleUpdateMaterial = async (updatedMaterial: MaterialData) => {
    await refreshMaterials();
  };

  const handleUpload = (files: FileList | null) => {
    // Handle file upload
    refreshMaterials();
  };

  // Handle document operations
  const handleDocumentClick = (material: MaterialData) => {
    setSelectedMaterial(material);
    setIsDocumentModalVisible(true);
  };
  const handleDocumentDownload = (document: any) => {
    // Close the modal first
    setIsDocumentModalVisible(false);

    if (onOpenViewer) {
      onOpenViewer(document);
    } else if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDeleteDocument = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteDocumentLoading(true);
    try {
      // Get materialTraceabilityId from the document
      const materialTraceabilityId = documentToDelete.id || documentToDelete.materialTraceabilityId;
      
      if (!materialTraceabilityId) {
        console.error('Material traceability ID not found', documentToDelete);
        setDeleteDocumentLoading(false);
        return;
      }

      const payload = {
        materialDocsIds: [Number(materialTraceabilityId)]
      };

      const deleteResponse = await DeleteMaterialDocument(payload);

      if (deleteResponse) {
        // Refresh materials after successful deletion
        refreshMaterials();
        // Close the delete modal
        handleCancelDeleteDocument();
        // Close the document modal if it's open
        setIsDocumentModalVisible(false);
        setSelectedMaterial(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  // Helper function to transform documents for the modal
  const transformDocumentsForModal = (material: MaterialData) => {
    const documents = material.documents || [];
    return documents.flatMap(doc => {
      // Handle the new API structure with materials array
      if ((doc as any).materials && Array.isArray((doc as any).materials)) {
        return (doc as any).materials.map((mat: any) => ({
          id: mat.materialTraceabilityId || Math.random().toString(),
          fileName: mat.fileName,
          fileUrl: mat.fileUrl,
          uploadDate: mat.uploadedAt || mat.createdAt,
          type: 'document'
        }));
      }
      // Handle legacy structure
      return [{
        id: (doc as any).id || Math.random().toString(),
        fileName: (doc as any).fileName,
        fileUrl: (doc as any).fileUrl,
        uploadDate: (doc as any).uploadedAt,
        type: 'document'
      }];
    });
  };

  const renderItemDetails = (item: DeleteItem) => {
    return (
      <div className="text-sm text-gray-700 py-2 border-b border-gray-200 last:border-b-0">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div>• Lot Number: {item.details?.wireLotNumber}</div>
          <div>• Serial Number: {item.details?.wireSerialNumber}</div>
          <div>• Manufacturer: {item.details?.wireExtraDetails?.wireManufacturer}</div>
          <div>• Batch Number: {item.details?.wireBatchNumber}</div>
        </div>
      </div>
    );
  };



  //Check if there are any specifications for this subtype
  const hasAnyMaterials = wireMaterials.length > 0;

  // If no specifications exist for any pipes in this subtype, show empty state
  if (!hasActiveFilters && !hasAnyMaterials) {
    return (
      <div className="space-y-6">

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Wrench className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No wires created yet</h3>
          <p className="text-gray-500 max-w-sm">
            Start by adding wires for your wires project. Click the "Add Material" button to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Wire Filters */}
        <WireFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueLotNumbers={uniqueLotNumbers}
          uniqueSerialNumbers={uniqueSerialNumbers}
          uniqueManufacturers={uniqueManufacturers}
          uniqueBatchNumbers={uniqueBatchNumbers}
          hasActiveFilters={hasActiveFilters}
          filteredCount={wireMaterials.length}
          onResetFilters={onResetFilters}
        />

        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table className="min-w-[1300px]" containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                      <TableHead>Consumable Type</TableHead>
                      <TableHead>Lot/Certification Number</TableHead>

                      <TableHead>Size</TableHead>
                      <TableHead className="text-center">Classification</TableHead>
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Batch Number</TableHead>

                    
                      <TableHead className="text-center w-[100px] ">Date</TableHead>
                      <TableHead className="text-center">Documents</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wireMaterials.length > 0 ? wireMaterials
                      .slice((currentPage - 1) * materialsPerPage, currentPage * materialsPerPage)
                      .map((wire) => (
                        <TableRow key={wire.materialId}>
                          <TableCell>{wire.details?.wireType}</TableCell>
                          <TableCell>{wire.details?.wireLotNumber}</TableCell>

                          <TableCell>{wire.details?.wireExtraDetails?.wireSizeNumber}</TableCell>

                          <TableCell>{wire.details?.wireExtraDetails?.wireClassification}</TableCell>
                          <TableCell>{wire.details?.wireExtraDetails?.wireManufacturer}</TableCell>
                          <TableCell>{wire.details?.wireBatchNumber}</TableCell>
                          {/* <TableCell>
                    <span className={cn(
                      "px-2 py-1 text-xs rounded-full",
                      wire.materialStatus === 'Available' && "text-galvanizing-green bg-[rgba(31,154,58,0.08)]",
                      wire.materialStatus === 'Consumed' && "bg-gray-100 text-gray-800"
                    )}>
                      {wire.materialStatus}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {wire.materialStatus === 'Consumed' ?
                      wire.assemblyNumbers?.join(', ') || '-' :
                      '-'
                    }
                  </TableCell> */}

                          <TableCell className="text-center w-[150px]">{wire.materialDate ? formatDate(wire.materialDate) : '-'}</TableCell>
                          <TableCell className="text-center w-[80px]">
                            <div className={`flex items-center gap-2 ${!wire.documents || wire.documents.length === 0 ? 'justify-center' : ''}`}>
                              <Button
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                onClick={() => {
                                  setSelectedMaterial(wire);
                                  setIsUploadModalOpen(true);
                                }}
                                title={`Upload ${wire.materialCategory}`}
                                aria-label={`Upload ${wire.materialCategory}`}
                                variant="outline"
                                disabled={!canEdit}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                              {wire.documents && wire.documents.length > 0 &&
                                <Button
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                  title="View Documents"
                                  variant="outline"
                                  onClick={() => handleDocumentClick(wire)}
                                >
                                  <FileText className="h-4 w-4" />
                                  {wire.documents.length}
                                </Button>
                              }
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Button
                                onClick={() => {
                                  setItemToUpdate(wire);
                                  setIsUpdateModalVisible(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                title="Edit Material"
                                variant="outline"
                                disabled={!canEdit || deletingRows?.has(String(wire.materialId))}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                onClick={() => {
                                  setIsDeleteModalVisible(true);
                                  setItemToDelete(wire)
                                }}
                                title="Delete Material"
                                variant="outline"
                                disabled={!canEdit || deletingRows?.has(String(wire.materialId))}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-gray-500">No wire materials found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={wireMaterials.length}
              itemsPerPage={materialsPerPage}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
              pageSizeOptions={[10, 25, 50, 100]}
              showPageNumbers={true}
              maxVisiblePages={5}
            />
          </CardContent>
        </Card>
        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={() => setIsDeleteModalVisible(false)}
          onConfirm={handleConfirmDelete}
          itemType="Material"
          itemName={itemToDelete?.details?.wireLotNumber}
          itemToDelete={itemToDelete}
          detailsRenderer={renderItemDetails}
          isLoading={deleteLoading}
        />
        <UpdateMaterialDialog
          isOpen={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          onUpdate={handleUpdateMaterial}
          material={itemToUpdate}
          project={{} as any}
          unit="inches"
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          selectedPipe={pipeSizesData}
        />

        {/* Document Download Modal */}
        {selectedMaterial && (
          <DocumentDownloadModal
            isOpen={isDocumentModalVisible}
            onClose={() => {
              setIsDocumentModalVisible(false);
              setSelectedMaterial(null);
            }}
            documents={transformDocumentsForModal(selectedMaterial)}
            onDownload={handleDocumentDownload}
            onDelete={handleDeleteDocument}
            title="Wire Material Documents"
            canDelete={canEdit}
          />
        )}

        {/* Document Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={isDocumentDeleteModalOpen}
          onClose={handleCancelDeleteDocument}
          onConfirm={handleConfirmDeleteDocument}
          isLoading={deleteDocumentLoading}
          itemToDelete={documentToDelete}
          itemType="Material Document"
          itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
          title="Delete Material Document"
        />

        {/* Document Upload Modal */}
        <MaterialDocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleUpload}
          title="Upload Material Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={1}
          buttonText="Select Document"
          label="Document"
          description="Upload material documents (PDF, Excel, DOC, etc...)"
          material={selectedMaterial || undefined}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          materialType="Wire"
          selectedPipeSize={pipeSizesData}
        />
      </div>
    </>
  );
}); 